import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Users, Clock, DollarSign, CheckCircle, Download, Lock, Plus } from 'lucide-react';

interface WorkHourWithProfile {
  id: string;
  user_id: string;
  date: string;
  start_time: string;
  end_time: string;
  break_minutes: number;
  task_description: string;
  project_code: string;
  total_hours: number;
  hourly_rate_chf: number;
  gross_pay_chf: number;
  status: string;
  paid_status: string;
  created_at: string;
  profiles: {
    full_name: string;
  } | null;
}

interface PayPeriod {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  locked: boolean;
}

const AdminPayroll = () => {
  const { user, loading, userRole } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [workHours, setWorkHours] = useState<WorkHourWithProfile[]>([]);
  const [payPeriods, setPayPeriods] = useState<PayPeriod[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentFilter, setPaymentFilter] = useState<string>('all');
  const [employeeFilter, setEmployeeFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);

  useEffect(() => {
    if (user) {
      fetchPayPeriods();
      fetchWorkHours();
    }
  }, [user]);

  // Mise à jour en temps réel des nouvelles soumissions d'heures
  useEffect(() => {
    if (user && (userRole === 'ADMIN' || userRole === 'admin')) {
      const channel = supabase
        .channel('admin-work-hours-updates')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'work_hours'
          },
          (payload) => {
            console.log('Nouvelles heures soumises:', payload);
            // Recharger les données quand de nouvelles heures sont soumises
            fetchWorkHours();
            toast({
              title: "Nouvelles heures reçues",
              description: "Un employé a soumis de nouvelles heures de travail."
            });
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public', 
            table: 'work_hours'
          },
          (payload) => {
            console.log('Heures mises à jour:', payload);
            // Recharger les données quand les heures sont mises à jour
            fetchWorkHours();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user, userRole]);

  // Redirect if not authenticated or not admin
  if (!loading && (!user || (userRole !== 'ADMIN' && userRole !== 'admin'))) {
    return <Navigate to="/auth" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-je-red mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const fetchPayPeriods = async () => {
    try {
      const { data, error } = await supabase
        .from('pay_periods')
        .select('*')
        .order('start_date', { ascending: false });

      if (error) throw error;
      setPayPeriods(data || []);
      if (data && data.length > 0 && !selectedPeriod) {
        setSelectedPeriod(data[0].id);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch pay periods"
      });
    }
  };

  const fetchWorkHours = async () => {
    try {
      // Récupérer les work_hours avec les profils des utilisateurs
      const { data: workHoursData, error: workError } = await supabase
        .from('work_hours')
        .select('*')
        .order('date', { ascending: false });

      if (workError) throw workError;

      // Récupérer tous les profils
      const { data: profilesData, error: profileError } = await supabase
        .from('profiles')
        .select('user_id, full_name');

      if (profileError) throw profileError;

      // Joindre manuellement les données
      const workHoursWithProfiles = (workHoursData || []).map(workHour => {
        const profile = profilesData?.find(p => p.user_id === workHour.user_id);
        return {
          ...workHour,
          profiles: profile ? { full_name: profile.full_name } : null
        };
      });

      setWorkHours(workHoursWithProfiles as WorkHourWithProfile[]);
    } catch (error: any) {
      console.error('Erreur lors de la récupération des heures:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de récupérer les heures de travail"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (entryIds: string[], newStatus: string) => {
    try {
      const { error } = await supabase
        .from('work_hours')
        .update({ status: newStatus })
        .in('id', entryIds);

      if (error) throw error;

      toast({
        title: "Succès",
        description: `${entryIds.length} entrées ${newStatus === 'approved' ? 'approuvées' : newStatus === 'rejected' ? 'rejetées' : 'mises à jour'}`
      });

      fetchWorkHours();
      setSelectedEntries([]);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    }
  };

  const handlePaymentStatusUpdate = async (entryIds: string[], paymentStatus: string) => {
    try {
      const { error } = await supabase
        .from('work_hours')
        .update({ paid_status: paymentStatus })
        .in('id', entryIds);

      if (error) throw error;

      toast({
        title: "Succès",
        description: `${entryIds.length} entrées marquées comme ${paymentStatus === 'paid' ? 'payées' : 'impayées'}`
      });

      fetchWorkHours();
      setSelectedEntries([]);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur", 
        description: error.message
      });
    }
  };

  const handleDeleteEntries = async (entryIds: string[]) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer ${entryIds.length} entrée(s) ? Cette action est irréversible.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('work_hours')
        .delete()
        .in('id', entryIds);

      if (error) throw error;

      toast({
        title: "Succès",
        description: `${entryIds.length} entrée(s) supprimée(s)`
      });

      fetchWorkHours();
      setSelectedEntries([]);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    }
  };

  const handleHourlyRateUpdate = async (entryId: string, newRate: number) => {
    try {
      const { error } = await supabase
        .from('work_hours')
        .update({ hourly_rate_chf: newRate })
        .eq('id', entryId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Hourly rate updated"
      });

      fetchWorkHours();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    }
  };

  const lockPayPeriod = async (periodId: string) => {
    try {
      const { error } = await supabase
        .from('pay_periods')
        .update({ locked: true })
        .eq('id', periodId);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Période de paie verrouillée"
      });

      fetchPayPeriods();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    }
  };

  const filteredWorkHours = workHours.filter(entry => {
    if (selectedPeriod) {
      const period = payPeriods.find(p => p.id === selectedPeriod);
      if (period) {
        const entryDate = new Date(entry.date);
        const startDate = new Date(period.start_date);
        const endDate = new Date(period.end_date);
        if (entryDate < startDate || entryDate > endDate) return false;
      }
    }

    if (statusFilter !== 'all' && entry.status !== statusFilter) return false;
    if (paymentFilter !== 'all' && entry.paid_status !== paymentFilter) return false;
    if (employeeFilter !== 'all' && entry.user_id !== employeeFilter) return false;

    return true;
  });

  const uniqueEmployees = Array.from(new Set(workHours.map(entry => entry.user_id)))
    .map(userId => {
      const entry = workHours.find(e => e.user_id === userId);
      return {
        id: userId,
        name: entry?.profiles?.full_name || 'Inconnu'
      };
    });

  const selectedPeriodData = payPeriods.find(p => p.id === selectedPeriod);

  // Calculate summary stats
  const totalHours = filteredWorkHours.reduce((sum, entry) => sum + parseFloat(entry.total_hours.toString()), 0);
  const totalPay = filteredWorkHours.reduce((sum, entry) => sum + parseFloat(entry.gross_pay_chf.toString()), 0);
  const uniqueEmployeesCount = new Set(filteredWorkHours.map(entry => entry.user_id)).size;
  const pendingApprovals = filteredWorkHours.filter(entry => entry.status === 'submitted').length;
  const unpaidEntries = filteredWorkHours.filter(entry => entry.paid_status === 'unpaid').length;

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'draft': return 'secondary';
      case 'submitted': return 'default';
      case 'approved': return 'default';
      case 'rejected': return 'destructive';
      default: return 'secondary';
    }
  };

  const getPaymentBadgeVariant = (paidStatus: string) => {
    switch (paidStatus) {
      case 'paid': return 'default';
      case 'unpaid': return 'destructive';
      default: return 'secondary';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">JE-HEPIA</h1>
              <div className="ml-6">
                <span className="text-sm text-gray-500">Gestion de la Paie</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost"
                onClick={() => navigate('/admin/users')}
                className="text-gray-600 hover:text-je-red"
              >
                Gestion Utilisateurs
              </Button>
              <span className="text-sm text-gray-700">
                Admin: {user?.email}
              </span>
              <Button 
                variant="outline" 
                onClick={() => supabase.auth.signOut()}
              >
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="flex items-center p-6">
              <Clock className="h-8 w-8 text-je-red mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Total Heures</p>
                <p className="text-2xl font-bold text-gray-900">{totalHours.toFixed(2)}h</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <DollarSign className="h-8 w-8 text-je-red mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Total Salaire</p>
                <p className="text-2xl font-bold text-gray-900">{totalPay.toFixed(2)} CHF</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <Users className="h-8 w-8 text-je-red mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Employés</p>
                <p className="text-2xl font-bold text-gray-900">{uniqueEmployeesCount}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <CheckCircle className="h-8 w-8 text-je-red mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Impayés</p>
                <p className="text-2xl font-bold text-gray-900">{unpaidEntries}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Actions */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Filtres et Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <Label>Période de paie</Label>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir une période" />
                  </SelectTrigger>
                  <SelectContent>
                    {payPeriods.map((period) => (
                      <SelectItem key={period.id} value={period.id}>
                        {period.name} {period.locked && '🔒'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Employé</Label>
                <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les employés" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les employés</SelectItem>
                    {uniqueEmployees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Statut Travail</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les statuts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="submitted">Soumis</SelectItem>
                    <SelectItem value="approved">Approuvé</SelectItem>
                    <SelectItem value="rejected">Rejeté</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Statut Paiement</Label>
                <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous paiements" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="paid">Payé</SelectItem>
                    <SelectItem value="unpaid">Impayé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedEntries.length > 0 && (
              <div className="flex gap-2 mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => handleStatusUpdate(selectedEntries, 'approved')}
                >
                  Approuver Sélection
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleStatusUpdate(selectedEntries, 'rejected')}
                >
                  Rejeter Sélection
                </Button>
                <Button 
                  variant="default" 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handlePaymentStatusUpdate(selectedEntries, 'paid')}
                >
                  💰 Marquer Payé
                </Button>
                <Button 
                  variant="outline" 
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={() => handlePaymentStatusUpdate(selectedEntries, 'unpaid')}
                >
                  ❌ Marquer Impayé
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => handleDeleteEntries(selectedEntries)}
                >
                  🗑️ Supprimer
                </Button>
              </div>
            )}

            {selectedPeriodData && !selectedPeriodData.locked && (
              <div className="mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => lockPayPeriod(selectedPeriod)}
                  className="text-orange-600 border-orange-600 hover:bg-orange-50"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Lock Pay Period
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Work Hours Table */}
        <Card>
          <CardHeader>
            <CardTitle>Work Hours</CardTitle>
            <CardDescription>
              {filteredWorkHours.length} entries 
              {selectedPeriodData && ` for ${selectedPeriodData.name}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">Loading...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input 
                        type="checkbox" 
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEntries(filteredWorkHours.map(entry => entry.id));
                          } else {
                            setSelectedEntries([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Employé</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Heures</TableHead>
                    <TableHead>Taux (CHF)</TableHead>
                    <TableHead>Salaire (CHF)</TableHead>
                    <TableHead>Statut Travail</TableHead>
                    <TableHead>Paiement</TableHead>
                    <TableHead>Projet</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWorkHours.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <input 
                          type="checkbox" 
                          checked={selectedEntries.includes(entry.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEntries([...selectedEntries, entry.id]);
                            } else {
                              setSelectedEntries(selectedEntries.filter(id => id !== entry.id));
                            }
                          }}
                        />
                      </TableCell>
                       <TableCell className="font-medium">
                         {entry.profiles?.full_name || 'Inconnu'}
                       </TableCell>
                       <TableCell>{entry.date}</TableCell>
                       <TableCell>{entry.total_hours}h</TableCell>
                       <TableCell>
                         <Input
                           type="number"
                           step="0.01"
                           value={entry.hourly_rate_chf}
                           onChange={(e) => {
                             const newRate = parseFloat(e.target.value);
                             if (newRate > 0) {
                               handleHourlyRateUpdate(entry.id, newRate);
                             }
                           }}
                           className="w-20"
                         />
                       </TableCell>
                       <TableCell>{entry.gross_pay_chf}</TableCell>
                       <TableCell>
                         <Badge variant={getStatusBadgeVariant(entry.status)}>
                           {entry.status === 'draft' ? 'Brouillon' : 
                            entry.status === 'submitted' ? 'Soumis' :
                            entry.status === 'approved' ? 'Approuvé' :
                            entry.status === 'rejected' ? 'Rejeté' : entry.status}
                         </Badge>
                       </TableCell>
                       <TableCell>
                         <Badge 
                           variant={getPaymentBadgeVariant(entry.paid_status)} 
                           className="cursor-pointer"
                           onClick={() => handlePaymentStatusUpdate([entry.id], entry.paid_status === 'paid' ? 'unpaid' : 'paid')}
                         >
                           {entry.paid_status === 'paid' ? '💰 Payé' : '❌ Impayé'}
                         </Badge>
                       </TableCell>
                      <TableCell>{entry.project_code || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {entry.status === 'submitted' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleStatusUpdate([entry.id], 'approved')}
                              >
                                ✓
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleStatusUpdate([entry.id], 'rejected')}
                              >
                                ✗
                              </Button>
                            </>
                          )}
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleDeleteEntries([entry.id])}
                            className="ml-2"
                          >
                            🗑️
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPayroll;